import { useCallback, useEffect, useRef, useState } from "react";
import type { CmmdTaskHistory } from "@cmmd-forge/contracts";

import { createCmmdApiClient } from "~/environments/cmmdApi";
import { CmmdApiError } from "~/environments/cmmdApi/transport";
import { sanitizePublicUiError } from "~/productSafeLabels";

const api = createCmmdApiClient();

export function useTaskHistory(taskId: number | null) {
  const [history, setHistory] = useState<CmmdTaskHistory | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scopeRef = useRef(taskId);
  const requestRef = useRef(0);
  scopeRef.current = taskId;

  useEffect(() => {
    requestRef.current += 1;
    setHistory(null);
    setLoading(false);
    setError(null);
  }, [taskId]);

  const load = useCallback(async () => {
    if (taskId === null) return;
    const requestId = ++requestRef.current;
    setLoading(true);
    setError(null);
    try {
      const nextHistory = await api.getTaskHistory(taskId);
      if (!isCurrentHistoryRequest(scopeRef.current, requestRef.current, taskId, requestId)) return;
      setHistory(nextHistory);
    } catch (cause) {
      if (!isCurrentHistoryRequest(scopeRef.current, requestRef.current, taskId, requestId)) return;
      // Authorization changes revoke previously visible history. A scoped 404 can be
      // transient, so retain an already-authorized snapshot and label the failure.
      if (shouldDiscardHistory(cause)) setHistory(null);
      setError(sanitizePublicUiError(cause, "Task history is unavailable."));
    } finally {
      if (isCurrentHistoryRequest(scopeRef.current, requestRef.current, taskId, requestId)) {
        setLoading(false);
      }
    }
  }, [taskId]);

  return { history, loading, error, load } as const;
}

function isCurrentHistoryRequest(
  activeTaskId: number | null,
  activeRequestId: number,
  taskId: number,
  requestId: number,
): boolean {
  return activeTaskId === taskId && activeRequestId === requestId;
}

function shouldDiscardHistory(cause: unknown): boolean {
  return cause instanceof CmmdApiError && (cause.status === 401 || cause.status === 403);
}
